/**
 * V93/V94 (Fase 6/6.1) — picker VISUAL de lugar: plano del aula, COMPARTIDO
 * entre el canal público (reservar/reagendar) y el staff (reserva manual y
 * Mover). V94: posicionamiento LIBRE (lienzo 80×80 unidades, footprint 4×4)
 * + orientación del aula (frente/espejos/entrada).
 *
 * Tomado (reserva u hold de oferta V90) = 🔒 no seleccionable. disabledAll:
 * el cupo AGREGADO del aula se agotó → todo se deshabilita.
 *
 * Render: se recorta al bounding-box de los lugares (los mapas compactos se
 * ven grandes); los marcadores de orientación se pintan sobre el BORDE
 * visible en proporción a su posición sobre la pared completa (aprox. — el
 * valor para el cliente es "qué pared", no milímetros; exactitud total en el
 * editor, que pinta el lienzo completo).
 */

export const CANVAS_UNITS = 80;      // lienzo lógico 80×80 (20 celdas V93 × 4)
export const SPOT_UNITS = 4;         // footprint de un lugar (la celda V93)

export type SpotCell = {
  id: string; label: string; row: number; col: number;
  /** V94 — coordenada fina; ausente en respuestas viejas → se deriva ×4. */
  x?: number; y?: number;
  taken: boolean;
};

export type LayoutEdge = 'top' | 'bottom' | 'left' | 'right';
export type RoomLayout = {
  front?: LayoutEdge;
  marks?: Array<{ type: 'mirror' | 'door'; edge: LayoutEdge; start: number; len: number }>;
} | null;

export const spotXY = (s: SpotCell) => ({
  x: s.x ?? s.col * SPOT_UNITS,
  y: s.y ?? s.row * SPOT_UNITS,
});

/** Mismo algoritmo que el backend (condición #1): cajas 4×4 se enciman. */
export const spotsCollide = (
  a: { x: number; y: number }, b: { x: number; y: number },
): boolean => Math.abs(a.x - b.x) < SPOT_UNITS && Math.abs(a.y - b.y) < SPOT_UNITS;

const EDGE_ES: Record<LayoutEdge, string> = {
  top: 'arriba', bottom: 'abajo', left: 'a la izquierda', right: 'a la derecha',
};

/** "Frente arriba · espejos arriba · entrada a la izquierda" (para el picker). */
export function layoutLegend(layout: RoomLayout): string | null {
  if (!layout) return null;
  const parts: string[] = [];
  if (layout.front) parts.push(`Frente ${EDGE_ES[layout.front]}`);
  const mirrors = [...new Set((layout.marks ?? []).filter((m) => m.type === 'mirror').map((m) => EDGE_ES[m.edge]))];
  if (mirrors.length) parts.push(`espejos ${mirrors.join(' y ')}`);
  const doors = [...new Set((layout.marks ?? []).filter((m) => m.type === 'door').map((m) => EDGE_ES[m.edge]))];
  if (doors.length) parts.push(`entrada ${doors.join(' y ')}`);
  return parts.length ? parts.join(' · ') : null;
}

/** Chrome de orientación sobre los bordes del canvas (editor Y picker). */
export function LayoutChrome({ layout }: { layout: RoomLayout }) {
  if (!layout) return null;
  const segStyle = (edge: LayoutEdge, start: number, len: number, cls: string, thick: string) => {
    const a = `${(start / CANVAS_UNITS) * 100}%`;
    const l = `${(len / CANVAS_UNITS) * 100}%`;
    const base = 'absolute pointer-events-none ' + cls;
    switch (edge) {
      case 'top':    return { className: base, style: { left: a, width: l, top: 0, height: thick } };
      case 'bottom': return { className: base, style: { left: a, width: l, bottom: 0, height: thick } };
      case 'left':   return { className: base, style: { top: a, height: l, left: 0, width: thick } };
      default:       return { className: base, style: { top: a, height: l, right: 0, width: thick } };
    }
  };
  const frontStyle = (edge: LayoutEdge) => {
    const base = 'absolute pointer-events-none flex items-center justify-center bg-slate-800 text-[9px] font-semibold uppercase tracking-widest text-white';
    switch (edge) {
      case 'top':    return { className: base, style: { left: 0, right: 0, top: 0, height: 14 } };
      case 'bottom': return { className: base, style: { left: 0, right: 0, bottom: 0, height: 14 } };
      case 'left':   return { className: `${base} [writing-mode:vertical-rl]`, style: { top: 0, bottom: 0, left: 0, width: 14 } };
      default:       return { className: `${base} [writing-mode:vertical-rl]`, style: { top: 0, bottom: 0, right: 0, width: 14 } };
    }
  };
  return (
    <>
      {layout.front && (() => { const p = frontStyle(layout.front); return <div className={p.className} style={p.style}>Frente</div>; })()}
      {(layout.marks ?? []).map((m, i) => {
        const p = m.type === 'mirror'
          ? segStyle(m.edge, m.start, m.len, 'bg-cyan-300/80', '5px')
          : segStyle(m.edge, m.start, m.len, 'bg-amber-500', '5px');
        return (
          <div key={i} className={p.className} style={p.style as any}
               title={m.type === 'mirror' ? 'Espejos' : 'Entrada'} />
        );
      })}
    </>
  );
}

export function SpotMapPicker({ spots, layout = null, value, onChange, disabledAll, accent }: {
  spots: SpotCell[]; layout?: RoomLayout; value: string; onChange: (id: string) => void;
  disabledAll?: boolean; accent?: string | null;
}) {
  if (spots.length === 0) return null;
  const pts = spots.map((s) => ({ s, ...spotXY(s) }));
  // Recorte al contenido (+2u de aire), clamp al lienzo.
  const pad = 2;
  const minX = Math.max(0, Math.min(...pts.map((p) => p.x)) - pad);
  const minY = Math.max(0, Math.min(...pts.map((p) => p.y)) - pad);
  const maxX = Math.min(CANVAS_UNITS, Math.max(...pts.map((p) => p.x + SPOT_UNITS)) + pad);
  const maxY = Math.min(CANVAS_UNITS, Math.max(...pts.map((p) => p.y + SPOT_UNITS)) + pad);
  const bw = Math.max(SPOT_UNITS + pad * 2, maxX - minX);
  const bh = Math.max(SPOT_UNITS + pad * 2, maxY - minY);
  const color = accent || '#0F172A';
  const legend = layoutLegend(layout);
  // Bug 2 (2026-07-22) — fit responsivo, mobile-first (el picker público es
  // el money-path en teléfono): ancho al contenedor, altura acotada a ~55vh
  // (mapas ALTOS no desbordan el modal: el ancho se deriva de la proporción),
  // tope 560px en desktop (el modal del staff dejaba de verse gigante).
  // Bug 2, 3ª vuelta — mismo tratamiento de tile que el editor: mínimo
  // LEGIBLE de 52px por columna (etiqueta completa a 2 líneas, sin truncar);
  // si el ancho de móvil no aguanta todas las columnas al mínimo, el wrapper
  // scrollea horizontal (último recurso: legibilidad > sin-scroll > vacío).
  const widthCss = `min(100%, ${Math.max(20, Math.round((bw / bh) * 55))}vh, 560px)`;
  const MIN_TILE_PX = 52;
  const minCanvasPx = Math.ceil((bw / SPOT_UNITS) * MIN_TILE_PX);
  return (
    <div>
      <div className="overflow-x-auto">
        <div className="relative rounded-xl border border-slate-300 bg-slate-50"
             style={{ width: widthCss, minWidth: `${minCanvasPx}px`, aspectRatio: `${bw} / ${bh}` }}>
          <LayoutChrome layout={layout} />
          {pts.map(({ s, x, y }) => {
            const blocked = s.taken || disabledAll === true;
            const selected = value === s.id;
            return (
              <button key={s.id} type="button" disabled={blocked}
                onClick={() => onChange(selected ? '' : s.id)}
                style={{
                  position: 'absolute',
                  left: `${((x - minX) / bw) * 100}%`,
                  top: `${((y - minY) / bh) * 100}%`,
                  width: `${(SPOT_UNITS / bw) * 100}%`,
                  height: `${(SPOT_UNITS / bh) * 100}%`,
                  ...(selected ? { background: color, borderColor: color } : {}),
                }}
                className={`flex items-center justify-center rounded-lg border px-0.5 text-[10px] font-medium leading-tight
                  ${selected
                    ? 'text-white'
                    : blocked
                      ? 'border-slate-200 bg-slate-100 text-slate-400'
                      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'}`}>
                {/* Etiqueta completa a 2 líneas, sin truncar (mismo tile que
                    el editor) — con el mínimo de 52px "Reformer N" cabe. */}
                <span className="overflow-hidden break-words text-center"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {s.taken ? `🔒 ${s.label}` : s.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {legend && <p className="mt-1 text-[10px] text-slate-500">{legend}.</p>}
    </div>
  );
}
